// HeroSection.jsx — Revised for the donation/portfolio framing
// Changes vs previous:
//  - Mission tagline promoted from subtle below-subtitle line to a proper "mission badge" above the headline
//  - Trust badges rewritten honestly (Free during beta / Donate to keep using it / Built solo, in public)
//  - "/pricing" path removed from the upsell CTA — guests now go to /about (read the why) instead of /signup
//  - BETA_MODE flag kept (it controls the whole UI mode), but the production-mode branch is left alone in case Federico wants it later
//  - All credit-status, skeleton, loading-progress, and error logic preserved exactly

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import LogoRotator from './LogoRotator';
import '../styles/LogoRotator.css';
import { useAuth } from '../context/AuthContext';
import { GAEvents } from '../utils/analytics';

const BETA_MODE = true;

const HeroSection = ({
  url,
  setUrl,
  loadingAnalysis,
  error,
  noCredits,
  handleSubmit,
  testBackendConnection
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    hasFreeAnalysisUsed,
    hasLifetimeAccess,
    creditsAvailable,
    hasCredits,
    loading
  } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  const [displayCredits, setDisplayCredits] = useState(null);
  const [displayLifetime, setDisplayLifetime] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  const creditsTimeoutRef = useRef(null);
  const initialLoadRef = useRef(false);

  const analyzingSteps = [
    'analyzing_step_1',
    'analyzing_step_2',
    'analyzing_step_3',
    'analyzing_step_4',
    'analyzing_step_5',
    'analyzing_step_6'
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      setDisplayCredits(null);
      setDisplayLifetime(false);
      setShowSkeleton(false);
      initialLoadRef.current = false;
      return;
    }
    if (loading) {
      setShowSkeleton(true);
      return;
    }
    if (creditsTimeoutRef.current) clearTimeout(creditsTimeoutRef.current);
    if (!initialLoadRef.current) {
      setShowSkeleton(true);
      initialLoadRef.current = true;
    }
    creditsTimeoutRef.current = setTimeout(() => {
      setDisplayCredits(creditsAvailable);
      setDisplayLifetime(hasLifetimeAccess);
      setShowSkeleton(false);
    }, 500);
    return () => {
      if (creditsTimeoutRef.current) clearTimeout(creditsTimeoutRef.current);
    };
  }, [isAuthenticated, loading, creditsAvailable, hasLifetimeAccess]);

  useEffect(() => {
    if (!isAuthenticated) initialLoadRef.current = false;
  }, [isAuthenticated]);

  useEffect(() => {
    if (loadingAnalysis) {
      setCurrentStep(0);
      setProgress(0);
      const stepInterval = setInterval(() => {
        setCurrentStep((prev) => (prev < analyzingSteps.length - 1 ? prev + 1 : prev));
      }, 3000);
      const progressInterval = setInterval(() => {
        setProgress((prev) => (prev < 95 ? prev + 1 : prev));
      }, 180);
      return () => {
        clearInterval(stepInterval);
        clearInterval(progressInterval);
      };
    } else {
      setCurrentStep(0);
      setProgress(0);
    }
  }, [loadingAnalysis, analyzingSteps.length]);

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    if (e.nativeEvent.inputType === 'insertFromPaste') {
      GAEvents.heroUrlPaste(newUrl);
    }
  };

  const handleAnalyzeClick = () => {
    GAEvents.heroCtaClick(url.length > 0, isAuthenticated);
    handleSubmit();
  };

  // Credit status — same logic as before, but the "free analysis used" upsell
  // now points to /about (read the why) instead of /signup or /pricing.
  const renderCreditStatus = () => {
    if (showSkeleton) {
      return (
        <div className="hero-credit-status">
          <div className="hero-credit-skeleton">
            <div className="hero-credit-skeleton-icon"></div>
            <div className="hero-credit-skeleton-text">
              <div className="hero-credit-skeleton-line"></div>
              <div className="hero-credit-skeleton-line-small"></div>
            </div>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="hero-credit-status">
          {hasFreeAnalysisUsed() ? (
            <div className="hero-credit-upsell">
              <div className="hero-credit-content">
                <div className="hero-credit-icon">💜</div>
                <div className="hero-credit-text">
                  <p className="hero-credit-title">
                    {t('home.beta_try_title')}
                  </p>
                  <p className="hero-credit-subtitle">
                    {t('home.beta_try_subtitle')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/about')}
                className="hero-credit-cta"
              >
                {t('home.homepage_footer_block_cta_about')}
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="hero-credit-available hero-credit-centered">
              <div className="hero-credit-icon">🎁</div>
              <div className="hero-credit-text">
                <p className="hero-credit-title">
                  {t('home.beta_try_title')}
                </p>
                <p className="hero-credit-subtitle">
                  {t('home.beta_try_subtitle')}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (displayLifetime) {
      return (
        <div className="hero-credit-status hero-credit-status-success">
          <div className="hero-credit-available hero-credit-centered">
            <div className="hero-credit-icon">♾️</div>
            <div className="hero-credit-text">
              <p className="hero-credit-title">{t('home.hero_credit_lifetime_title')}</p>
              <p className="hero-credit-subtitle">{t('home.hero_credit_lifetime_subtitle')}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="hero-credit-status">
        <div className="hero-credit-available hero-credit-centered">
          <div className="hero-credit-icon">⚡</div>
          <div className="hero-credit-text">
            <p className="hero-credit-title">
              {displayCredits} {t('home.hero_credit_count_label')}
            </p>
            <p className="hero-credit-subtitle">
              {t('home.hero_credit_count_subtitle')}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const scrollToDemo = () => {
    GAEvents.heroSecondaryCtaClick('See How It Works');
    const demoSection = document.querySelector('.hiw-video-section');
    if (demoSection) demoSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-section-new">
      <div className="hero-background">
        <div className="hero-blob hero-blob-1"></div>
        <div className="hero-blob hero-blob-2"></div>
        <div className="hero-blob hero-blob-3"></div>
      </div>

      <div className="hero-container">
        <div className="hero-content">

          {/* Mission badge — promoted from subtle line to first thing you read */}
          <div className="hero-badge" style={{ marginBottom: '1.5rem' }}>
            <span>
              💜 {t('home.mission_tagline_text')}{' '}
              <Link
                to="/about"
                style={{
                  color: '#c084fc',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  fontWeight: 500,
                }}
              >
                {t('home.mission_tagline_link')}
              </Link>
            </span>
          </div>

          {/* Title — unchanged */}
          <h1 className="hero-title">
            {t('home.hero_title_new')}
          </h1>
          <h1 className="hero-title">
            <span className="hero-title-gradient">
              {t('home.hero_title_highlight_new')}
            </span>
          </h1>

          {/* Subtitle with logo rotator — unchanged */}
          <div className="hero-subtitle">
            <span>{t('home.hero_subtitle_new')}</span>
            <LogoRotator />
          </div>

          {/* Input card */}
          <div className="hero-card">
            <div className="hero-card-inner">

              <div className="hero-input-group">
                <input
                  type="url"
                  className="hero-input"
                  placeholder={t('home.hero_placeholder_optimized')}
                  value={url}
                  onChange={handleUrlChange}
                  disabled={loadingAnalysis}
                />
                <button
                  onClick={handleAnalyzeClick}
                  disabled={loadingAnalysis || loading}
                  className="hero-btn"
                >
                  {loadingAnalysis ? (
                    <>
                      <svg className="hero-spinner" viewBox="0 0 24 24">
                        <circle className="hero-spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="hero-spinner-path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('home.analyzing')}
                    </>
                  ) : (
                    <>
                      {t('home.hero_cta_primary')}
                      <svg className="hero-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </div>

              {loadingAnalysis && (
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{
                    textAlign: 'center',
                    color: '#93c5fd',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '1rem',
                    minHeight: '20px',
                    animation: 'fadeIn 0.5s ease-in-out'
                  }}>
                    {t(`home.${analyzingSteps[currentStep]}`)}
                  </div>
                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'rgba(148, 163, 184, 0.2)',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      borderRadius: '10px',
                      transition: 'width 0.3s ease',
                      boxShadow: '0 0 10px rgba(37, 99, 235, 0.45)'
                    }}></div>
                  </div>
                  <div style={{
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '0.75rem',
                    marginTop: '0.5rem'
                  }}>
                    {progress}%
                  </div>
                </div>
              )}

              {error && (
                <div className="hero-error">
                  <svg className="hero-error-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="hero-error-content">{error}</div>
                </div>
              )}

              {noCredits && (
                <div className="hero-error">
                  <svg className="hero-error-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="hero-error-content">
                    {t('home.no_credits_prefix') || 'No credits remaining.'}{' '}
                    <Link
                      to="/dashboard"
                      style={{ color: '#93c5fd', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      {t('home.no_credits_cta') || 'Donate to unlock more →'}
                    </Link>
                  </div>
                </div>
              )}

              {!loadingAnalysis && renderCreditStatus()}
            </div>
          </div>

          {/* Trust badges — rewritten honestly */}
          <div className="hero-badges">
            <div className="hero-badge-item">
              <svg className="hero-badge-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{t('home.hero_trust_beta_1')}</span>
            </div>
            <div className="hero-badge-item">
              <svg className="hero-badge-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
              <span>{t('home.hero_trust_beta_2')}</span>
            </div>
            <div className="hero-badge-item">
              <svg className="hero-badge-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{t('home.hero_trust_beta_3')}</span>
            </div>
          </div>

          {/* Secondary CTA — unchanged */}
          <div className="hero-secondary-cta-container">
            <button
              className="hero-secondary-cta"
              onClick={scrollToDemo}
            >
              {t('home.hero_secondary_cta')}
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;